import { sendUserEvent } from 'core/crud/analytics';
import { useUser } from 'core/lite/hooks/useUser';
import { parseEducationYearName } from 'core/lite/parsers';
import * as coreEntities from 'core/store/index';
import { emitAmplitudeEvent, isNativeAppWebview } from 'core/utils/utils';
import { AMPLITUDE_EVENT } from 'core/variables/constant';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { UAParser } from 'ua-parser-js';

const useAmplitude = () => {
  const client = useSelector((state) => coreEntities.organization.selectors.getClient(state));
  const getTenant = useSelector((state) => coreEntities.organization.selectors.getTenant(state));
  const currentCourse = useSelector((state) => coreEntities.courses.selectors.getCurrentCourse(state));
  const settings = useSelector((state) => coreEntities.systemSettings.selectors.getSystemSettings(state));

  const { user } = useUser();
  const ua = navigator.userAgent;
  const parser = new UAParser(ua);

  function sendAmplitudeEvent(event, extraProperties, course = currentCourse || {}) {
    const schoolName = user?.schools?.[0]?.name ?? null;

    const courseProperties = {
      discipline: course?.discipline_name,
      content_stage: course?.education_level_name,
      content_grade: parseEducationYearName(course?.education_year_name),
      course_guid: course?.guid,
      education_level_guid: course?.education_level_guid,
      education_year_guid: course?.education_year_guid,
      discipline_guid: course?.discipline_guid,
      collection_guid: course?.collections?.[0]?.guid,
      lang_guid: course?.digital_program_langs?.[0]?.id,
      country_guid: course?.country_guid,
      template_guid: course?.template_guid,
      program: course?.name,
      program_guid: course?.program_guid,
      school_name: schoolName,
    };
    emitAmplitudeEvent(event, {
      tenant: client,
      timestamp: new Date().getTime(),
      user_id: user?.guid,
      role_guid: user?.role_guid,
      is_app: isNativeAppWebview() ? 'yes' : 'no',
      school_state_guid: user?.schools?.length > 0 ? user?.schools[0]?.state_guid : null,
      school_city_guid: user?.schools?.length > 0 ? user?.schools[0]?.city_guid : null,
      school_guid: user?.schools?.length > 0 ? user?.schools[0]?.guid : null,
      ...courseProperties,
      ...extraProperties,
    });

    if (settings?.enable_advanced_programming && !course?.isReadOnly) {
      let userAgentProperties = getUserAgentProperties();

      sendUserEvent({
        event_type: event,
        tenant: getTenant,
        ocurred_on: moment().format('YYYY-MM-DDTHH:mm:ssZZ'),
        attributes: {
          tenant: client,
          timestamp: new Date().getTime(),
          user_id: user?.guid,
          role_guid: user?.role_guid,
          school_state_guid: user?.schools?.length > 0 ? user?.schools[0]?.state_guid : null,
          school_city_guid: user?.schools?.length > 0 ? user?.schools[0]?.city_guid : null,
          school_guid: user?.schools?.length > 0 ? user?.schools[0]?.guid : null,
          ...courseProperties,
          ...extraProperties,
          ...userAgentProperties,
        },
      });
    }
  }

  function getUserAgentProperties() {
    return {
      browser_name: parser?.getBrowser().name,
      browser_version: parser?.getBrowser().version,
      os_name: parser?.getOS().name,
      os_version: parser?.getOS().version,
      device_type: parser?.getDevice().type,
      device_vendor: parser?.getDevice().vendor,
      device_model: parser?.getDevice().model,
      cpu: parser?.getCPU().architecture,
      language: navigator.language,
    };
  }

  return { sendAmplitudeEvent, AMPLITUDE_EVENT };
};

export default useAmplitude;
